import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";

import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import StickyBookBar from "@/components/StickyBookBar";
import { trackPageView } from "@/lib/analytics";
import { useGA4 } from "@/lib/ga4";
import { useSiteVerifications } from "@/lib/verifications";

import Home from "@/pages/Home";
import Accommodations from "@/pages/Accommodations";
import Gallery from "@/pages/Gallery";
import About from "@/pages/About";
import LocalGuide from "@/pages/LocalGuide";
import Amenities from "@/pages/Amenities";
import Contact from "@/pages/Contact";
import Accessibility from "@/pages/Accessibility";
import Login from "@/pages/Login";
import Admin from "@/pages/Admin";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import FAQ from "@/pages/FAQ";

function ScrollToTop() {
  const { pathname } = useLocation();
  useGA4();
  useSiteVerifications();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    trackPageView(pathname);
    if (window.gtag) window.gtag("event", "page_view", { page_path: pathname });
  }, [pathname]);
  return null;
}

function PublicLayout({ children }) {
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
      <StickyBookBar />
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
            <Route path="/accommodations" element={<PublicLayout><Accommodations /></PublicLayout>} />
            <Route path="/gallery" element={<PublicLayout><Gallery /></PublicLayout>} />
            <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
            <Route path="/local-guide" element={<PublicLayout><LocalGuide /></PublicLayout>} />
            <Route path="/amenities" element={<PublicLayout><Amenities /></PublicLayout>} />
            <Route path="/accessibility" element={<PublicLayout><Accessibility /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
            <Route path="/blog" element={<PublicLayout><Blog /></PublicLayout>} />
            <Route path="/blog/:slug" element={<PublicLayout><BlogPost /></PublicLayout>} />
            <Route path="/faq" element={<PublicLayout><FAQ /></PublicLayout>} />

            {/* Legacy WordPress URL redirects — preserve link equity from old site */}
            <Route path="/about-us" element={<Navigate to="/about" replace />} />
            <Route path="/about-us/" element={<Navigate to="/about" replace />} />
            <Route path="/contact-us-hotels-in-carrollton-mo" element={<Navigate to="/contact" replace />} />
            <Route path="/contact-us-hotels-in-carrollton-mo/" element={<Navigate to="/contact" replace />} />
            <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
            <Route path="/hotels-in-carrollton-mo" element={<Navigate to="/accommodations" replace />} />
            <Route path="/hotels-in-carrollton-mo/" element={<Navigate to="/accommodations" replace />} />
            <Route path="/rooms" element={<Navigate to="/accommodations" replace />} />
            <Route path="/suites" element={<Navigate to="/accommodations" replace />} />
            <Route path="/front-page-2/ada-features" element={<Navigate to="/accessibility" replace />} />
            <Route path="/front-page-2/ada-features/" element={<Navigate to="/accessibility" replace />} />
            <Route path="/ada-features" element={<Navigate to="/accessibility" replace />} />
            <Route path="/ada" element={<Navigate to="/accessibility" replace />} />
            <Route path="/photos" element={<Navigate to="/gallery" replace />} />
            <Route path="/reservation" element={<Navigate to="/" replace />} />
            <Route path="/reservations" element={<Navigate to="/" replace />} />
            <Route path="/book" element={<Navigate to="/" replace />} />
            <Route path="/booking" element={<Navigate to="/" replace />} />
            <Route path="/amenities-policies" element={<Navigate to="/amenities" replace />} />
            <Route path="/policies" element={<Navigate to="/amenities" replace />} />
            <Route path="/pet-policy" element={<Navigate to="/amenities" replace />} />
            <Route path="/local-attractions" element={<Navigate to="/local-guide" replace />} />
            <Route path="/attractions" element={<Navigate to="/local-guide" replace />} />
            <Route path="/things-to-do" element={<Navigate to="/blog/things-to-do-in-carrollton-missouri" replace />} />
            <Route path="/news" element={<Navigate to="/blog" replace />} />
            <Route path="/articles" element={<Navigate to="/blog" replace />} />
            <Route path="/index.php" element={<Navigate to="/" replace />} />
            <Route path="/home" element={<Navigate to="/" replace />} />

            <Route path="/login" element={<Login />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <Admin />
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
